import { createHash } from "node:crypto";
import { lstat, open, realpath } from "node:fs/promises";
import { dirname, extname, isAbsolute, resolve } from "node:path";
import { z } from "zod";
import type { CopilotChannelApi } from "../api/copilot-channel-api.js";
import { COPILOT_CHANNEL_MAX_BYTES, hasChannelZipSignature } from "../api/copilot-channel-api.js";
import { ApiError, ValidationError } from "../utils/errors.js";
import { GUID_PATTERN, validateEnvironmentId } from "../utils/security.js";

export const downloadCopilotAgentChannelManifestInputSchema = z
  .object({
    agentId: z.string().regex(GUID_PATTERN, "agentId must be a GUID"),
    outputFilePath: z.string().min(1).max(4096),
    includeAgentSchema: z.boolean().default(false),
    confirmDownload: z.boolean().default(false),
  })
  .strict();

export const downloadCopilotAgentChannelManifestTool = {
  name: "download_copilot_agent_channel_manifest",
  description:
    "Download the official Microsoft 365 (M365) agent-channel manifest ZIP through Power Platform API 2024-10-01. Reads the tenant and creates one NEW local .zip file only; never publishes, enables a channel, imports, installs, or deploys anything. Requires confirmDownload=true, an absolute path with an existing canonical parent, and no existing destination. Maximum 16 MiB. includeAgentSchema defaults false. Uses the session's current environment. Access and channel prerequisites are enforced by the service; no scope is guessed or granted. ZIP signature/length/hash checks are not validation of package contents or runtime behavior.",
  inputSchema: {
    type: "object" as const,
    additionalProperties: false,
    properties: {
      agentId: { type: "string", pattern: GUID_PATTERN.source },
      outputFilePath: {
        type: "string",
        minLength: 1,
        maxLength: 4096,
        description:
          "Absolute path to a NEW .zip file. Parent must already exist and contain no symlink aliases. Existing files are never overwritten.",
      },
      includeAgentSchema: { type: "boolean", default: false },
      confirmDownload: { type: "boolean", default: false },
    },
    required: ["agentId", "outputFilePath"],
  },
};

async function validateDestination(path: string): Promise<void> {
  if (
    !isAbsolute(path) ||
    path.includes("\0") ||
    resolve(path) !== path ||
    extname(path).toLowerCase() !== ".zip"
  ) {
    throw new ValidationError(
      "outputFilePath must be a canonical absolute .zip path without traversal",
    );
  }
  const parent = dirname(path);
  if ((await realpath(parent)) !== parent)
    throw new ValidationError("Output parent must be canonical, not a symbolic-link alias");
  if (!(await lstat(parent)).isDirectory())
    throw new ValidationError("Output parent must be a directory");
  try {
    await lstat(path);
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return;
    throw error;
  }
  throw new ValidationError(
    "Output already exists; refusing to overwrite a file, directory, or symlink",
  );
}

export async function handleDownloadCopilotAgentChannelManifest(
  api: Pick<CopilotChannelApi, "downloadManifest">,
  input: unknown,
) {
  const parsed = downloadCopilotAgentChannelManifestInputSchema.parse(input);
  if (!parsed.confirmDownload)
    return {
      text: "No download performed. Set confirmDownload=true to read the M365 manifest and create one new local ZIP. This does not publish or install the agent.",
      structured: {
        downloaded: false,
        agentId: parsed.agentId,
        channel: "M365",
        includeAgentSchema: parsed.includeAgentSchema,
      },
    };
  await validateDestination(parsed.outputFilePath);
  const result = await api.downloadManifest(parsed.agentId, parsed.includeAgentSchema);
  if (
    !Buffer.isBuffer(result.bytes) ||
    !hasChannelZipSignature(result.bytes) ||
    result.bytes.length > COPILOT_CHANNEL_MAX_BYTES ||
    result.sizeBytes !== result.bytes.length ||
    result.sha256 !== createHash("sha256").update(result.bytes).digest("hex") ||
    result.agentId !== parsed.agentId.toLowerCase() ||
    result.channel !== "M365" ||
    result.includeAgentSchema !== parsed.includeAgentSchema
  ) {
    throw new ApiError(
      "Channel manifest metadata or bytes failed verification; no file saved",
      502,
    );
  }
  validateEnvironmentId(result.environmentId);
  await validateDestination(parsed.outputFilePath);
  // Exclusive create protects an existing destination, including a symlink.
  // A concurrent parent-directory replacement is not an atomic OS sandbox.
  const file = await open(parsed.outputFilePath, "wx+", 0o600);
  try {
    await file.writeFile(result.bytes);
    await file.sync();
    if ((await file.stat()).size !== result.sizeBytes)
      throw new ApiError("Channel manifest file size mismatch", 500);
    const readback = Buffer.alloc(result.sizeBytes);
    let offset = 0;
    while (offset < readback.length) {
      const { bytesRead } = await file.read(readback, offset, readback.length - offset, offset);
      if (!bytesRead) throw new ApiError("Channel manifest readback ended early", 500);
      offset += bytesRead;
    }
    if (createHash("sha256").update(readback).digest("hex") !== result.sha256)
      throw new ApiError("Channel manifest readback hash mismatch", 500);
    const descriptor = await file.stat();
    const destination = await lstat(parsed.outputFilePath);
    if (
      descriptor.size !== result.sizeBytes ||
      !destination.isFile() ||
      descriptor.ino !== destination.ino ||
      descriptor.dev !== destination.dev ||
      (await realpath(dirname(parsed.outputFilePath))) !== dirname(parsed.outputFilePath)
    )
      throw new ApiError("Channel manifest destination changed during write", 500);
  } catch {
    throw new ApiError(
      "Local manifest write/readback failed; a partial new file may remain at outputFilePath. No existing file was overwritten",
      500,
    );
  } finally {
    await file.close();
  }
  return {
    text: `Downloaded M365 channel manifest to ${parsed.outputFilePath} (${result.sizeBytes} bytes). SHA-256: ${result.sha256}. No tenant changes. Package contents, installation and runtime were not validated.`,
    structured: {
      downloaded: true,
      agentId: result.agentId,
      environmentId: result.environmentId,
      channel: result.channel,
      includeAgentSchema: result.includeAgentSchema,
      outputFilePath: parsed.outputFilePath,
      sizeBytes: result.sizeBytes,
      sha256: result.sha256,
      tenantChanged: false,
      packageContentsValidated: false,
    },
  };
}
